import type { ContentType } from "@amzn/g4sservice-client";
import { Text } from "@amzn/storm-ui";
import { useMountEffect } from "@react-hookz/web";
import { useState } from "react";
import { useStore } from "zustand";

import { ExploreWithAIFilters } from "../../components/discovery/filters/ExploreWithAIFilters";
import { PromptTextArea } from "../../components/discovery/insights/exploreWithAI/PromptTextArea";
import { SelectedContentViewer } from "../../components/discovery/insights/exploreWithAI/SelectedContentViewer";
import { XWAI_ENGLISH_CONTENTS_ONLY } from "../../config/constants";
import { useG4SContentSelectionLoader } from "../../hooks/insightsFromAI/useG4SContentSelectionLoader";
import { useInsightsFromAIFiltersLoader } from "../../hooks/insightsFromAI/useInsightsFromAIFiltersLoader";
import { exploreWithAiStore } from "../../store/exploreWithAi";
import { discoveryFilterStore } from "../../store/filters";
import { IHubContentType } from "../../types/ihubContentType";
import { IHubContentProvider } from "../../types/providers";
import { isLoading } from "../../utils/api.utils";
import {
    getAdvertiserId,
    getMarketplaceId,
} from "../../utils/permission.utils";
import { getInsightsExamplePrompts } from "../../utils/prompts.util";

export const ExploreWithAI: React.FunctionComponent = () => {
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [setContentType, setContentSubType] = useStore(
        discoveryFilterStore,
        (state) => [state.setContentType, state.setContentSubType],
    );
    const [
        setStorePrompt,
        setStoreIsExamplePrompt,
        storeContent,
        storeResponseId,
        storeAsyncStatus,
        setStoreBrandId,
        setStoreBrowseNodeId,
        setContentTypes,
    ] = useStore(exploreWithAiStore, (state) => [
        state.setPrompt,
        state.setIsExamplePrompt,
        state.selectedContent,
        state.responseId,
        state.asyncStatus,
        state.setBrandId,
        state.setBrowseNodeId,
        state.setContentTypes,
    ]);

    useMountEffect(async () => {
        setContentType(IHubContentType.INSIGHTS);
        setContentSubType(IHubContentType.INSIGHTS, {
            provider: IHubContentProvider.INSIGHTS_FROM_AI,
        });
    });

    const advertiserId = getAdvertiserId();
    const marketplaceId = getMarketplaceId();
    useG4SContentSelectionLoader({
        advertiserId,
        marketplaceId,
        isInitialLoad,
    });
    const [insightsFromAIFiltersStatus] = useInsightsFromAIFiltersLoader();

    const setPrompt = (
        value: string,
        isExamplePrompt: boolean,
        brandId?: string,
        browseNodeId?: string,
        contentTypes?: ContentType[],
    ) => {
        setIsInitialLoad(false);
        setStoreIsExamplePrompt(isExamplePrompt);
        setStorePrompt(value);
        setStoreBrandId(brandId);
        setStoreBrowseNodeId(browseNodeId);
        setContentTypes(contentTypes);
    };

    return (
        <>
            <PromptTextArea
                isLoading={isLoading(storeAsyncStatus)}
                onSubmit={setPrompt}
                placeholder="Describe your advertiser, objective, or desired insight"
                examplePrompts={getInsightsExamplePrompts({})}
                submitButtonText="Explore"
            />
            {/* Remove the text below once we are able to filter by language */}
            <Text withMargin weight="bold">
                {XWAI_ENGLISH_CONTENTS_ONLY}
            </Text>
            <ExploreWithAIFilters
                setIsInitialLoad={setIsInitialLoad}
                filterStatus={insightsFromAIFiltersStatus}
            />
            <SelectedContentViewer
                responseId={storeResponseId}
                asyncStatus={storeAsyncStatus}
                selectedContents={storeContent}
                totalResults={storeContent.length}
            />
        </>
    );
};
